import React from "react";
import {
    LayoutDashboard, Package, Contact, Building2, Users, FileText, ShoppingCart,
    Plug, Palette, User, Settings, BookOpen,
    ChevronRight, ChevronDown, ArrowRight, ArrowUp, ArrowDown, ArrowUpDown, ArrowDownToLine,
    Bell, Box, Check, X, Copy, Trash2, Pencil, Eye, EyeOff, Filter, Link, ExternalLink,
    LoaderCircle, Plus, PlusCircle, Search, Upload, Download, GripVertical, LogOut,
    TriangleAlert, PlugZap, Unplug, Save, Circle, CircleCheck,
    Mail, Phone, Send,
    Columns3, List, Table, Image, Calendar, Tag, Tags, Star,
    CalendarPlus, CalendarClock, MapPin, Milestone, UserCircle,
    MessageSquare, MessageCircle, ListChecks,
    OctagonAlert, RefreshCw, ZoomIn, EllipsisVertical,
    HelpCircle,
    DollarSign, Receipt, TrendingUp, Clock, Timer, Layers, Trophy, ChartColumn, Zap, ReceiptText,
    NotebookPen,
    Calculator,
    Menu,
    Waypoints,
    Lock,
    Sparkles,
    FileSignature, TicketPercent, CreditCard, MessageSquareQuote, Store,
    KeyRound, FileSpreadsheet
} from "lucide-react";

/**
 * Mapa dos nomes históricos de ícone (antigos arquivos .svg) para os
 * componentes do Lucide (https://lucide.dev). Mantém a mesma API de nome
 * usada em toda a aplicação (inclusive nos casos dinâmicos: sidebar,
 * `task-{tipo}-{status}` e `activity-{tipo}`), então o call site continua
 * passando a mesma string de antes.
 */
const ICONS = {
    // Navegação / sidebar
    dashboard: LayoutDashboard,
    product: Package,
    crm: Contact,
    company: Building2,
    people: Users,
    quote: FileText,
    order: ShoppingCart,
    integration: Plug,
    design: Palette,
    user: User,
    config: Settings,
    knowledgeBase: BookOpen,
    help: HelpCircle,
    menu: Menu,

    // Ações
    arrow: ChevronRight,
    "arrow-dropdown": ChevronDown,
    "arrow-gray": ChevronDown,
    "arrow-right-white": ArrowRight,
    bell: Bell,
    box: Box,
    check: Check,
    close: X,
    "close-red": X,
    copy: Copy,
    delete: Trash2,
    "delete-small": Trash2,
    "delete-small-white": Trash2,
    edit: Pencil,
    "edit-white": Pencil,
    eye: Eye,
    eyeoff: EyeOff,
    filter: Filter,
    "filter-white": Filter,
    link: Link,
    "external-link": ExternalLink,
    loading: LoaderCircle,
    "plus-blue": Plus,
    "plus-white": Plus,
    "plus-circle": PlusCircle,
    search: Search,
    sort: ArrowUpDown,
    sort_asc: ArrowUp,
    sort_desc: ArrowDown,
    upload: Upload,
    "upload-small": Upload,
    import: Download,
    export: Upload,
    drag: GripVertical,
    logout: LogOut,

    // Status / conexão
    alert: TriangleAlert,
    connected: PlugZap,
    disconnected: Unplug,
    "icon-connect": Plug,
    "icon-disconnect": Unplug,
    "icon-save": Save,
    save: Save,
    "save-white": Save,
    uncheck: Circle,

    // Contato
    contact: Contact,
    email: Mail,
    phone: Phone,
    send: Send,

    // Visualização
    "kanban-view": Columns3,
    "list-view": List,
    table: Table,
    image: Image,

    // Formulário / entrada
    calendar: Calendar,
    "calendar-light": Calendar,
    tag: Tag,
    tags: Tags,
    minimum: ArrowDownToLine,
    "new-integration": Plus,
    destaques: Star,
    "input-preview-cp": Copy,
    "input-preview-edit": Pencil,
    pdf: FileText,

    // Cartão / CRM
    "card-company": Building2,
    "card-date-created-at": CalendarPlus,
    "card-date-updated-at": CalendarClock,
    "card-location": MapPin,
    "card-origin": Milestone,
    "card-owner": UserCircle,
    "card-user": User,
    "card-stage-arrow": ChevronRight,
    "card-stage-arrow-hover": ChevronRight,

    // Atividades (activity-{tipo}) — orçamento/pedido espelham os ícones da sidebar
    "activity-budget": Calculator,
    "activity-comments": MessageSquare,
    "activity-order": ReceiptText,
    "activity-whatsapp": MessageCircle,

    // Tarefas (task-{tipo}-{status})
    "task-call-pending": Phone,
    "task-call-completed": Phone,
    "task-meeting-pending": CalendarClock,
    "task-meeting-completed": CalendarClock,
    "task-email-pending": Mail,
    "task-email-completed": Mail,
    "task-task-pending": ListChecks,
    "task-task-completed": ListChecks,
    "task-completed": CircleCheck,

    // Diversos (antes eram <svg> inline no JSX)
    refresh: RefreshCw,
    "zoom-in": ZoomIn,
    "more-vertical": EllipsisVertical,
    "octagon-alert": OctagonAlert,
    zap: Zap,
    sparkles: Sparkles,
    "receipt-text": ReceiptText,
    "notebook-pen": NotebookPen,
    "calculator": Calculator,
    "waypoints": Waypoints,
    lock: Lock,

    // Portal do fornecedor (só existem neste painel)
    api: KeyRound,
    xls: FileSpreadsheet,

    // Herdados do mapa compartilhado (podem não ter uso aqui)
    contract: FileSignature,
    coupon: TicketPercent,
    subscription: CreditCard,
    layers: Layers,
    plan: Layers,
    supplier: Store,
    testimonial: MessageSquareQuote,

    // Dashboard / métricas
    money: DollarSign,
    ticket: Receipt,
    trending: TrendingUp,
    clock: Clock,
    timer: Timer,
    items: Layers,
    trophy: Trophy,
    chart: ChartColumn
};

/**
 * <Icon name="dashboard" /> — renderiza o ícone Lucide correspondente.
 * A cor segue `currentColor` por padrão (herda do texto); o tamanho padrão é 20.
 * Aceita todas as props de um SVG (onClick, style, etc.).
 */
const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 2, className = "", ...rest }) => {
    const Component = ICONS[name] || HelpCircle;

    if (!ICONS[name] && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(`[Icon] ícone não mapeado: "${name}"`);
    }

    return (
        <Component
            size={size}
            color={color}
            strokeWidth={strokeWidth}
            className={`icon ${className}`.trim()}
            {...rest}
        />
    );
};

export default Icon;
